import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, Store, Shield, MapPin } from "lucide-react";
import { apiClient } from "../../services/apiClientService";
import Seo from "../common/Seo";
import { useVendorStore } from "../../context/VendorStoreContext";

const highlights = [
  {
    title: "Vendor empowerment",
    body: "Launch, manage, and grow with tools for inventory, orders, and your storefront.",
  },
  {
    title: "Built for shoppers",
    body: "Fast browsing, clear pricing, and checkout that works on phone and desktop.",
  },
  {
    title: "Payments you can trust",
    body: "Secure payment flows and order handling with privacy in mind.",
  },
  {
    title: "All-in-one stack",
    body: "Unified experience for your shop, customers, and operations.",
  },
  {
    title: "Always improving",
    body: "We iterate on speed, reliability, and usability so the platform keeps feeling modern.",
  },
];

function About() {
  const { vendorStore, storeSlug } = useVendorStore();
  const [storeLabel, setStoreLabel] = useState(null);

  useEffect(() => {
    // In vendor-slug mode, vendor data comes from context — no API call needed
    if (vendorStore) { setStoreLabel(null); return; }

    let mounted = true;
    const load = async () => {
      try {
        const res = await apiClient("GET", "/api/vendors/storefront", null, {
          skipLoading: true,
        });
        if (!mounted) return;
        const v = res?.singleVendor ? res.vendor : null;
        const name = v?.storeName?.trim() || v?.name?.trim() || "";
        setStoreLabel(name || null);
      } catch {
        if (mounted) setStoreLabel(null);
      }
    };
    load();
    return () => { mounted = false; };
  }, [vendorStore]);

  // ─── Vendor-slug mode: full vendor-branded about page ───────────────────────
  if (vendorStore) {
    const name = vendorStore.storeName?.trim() || vendorStore.name?.trim() || "Store";
    const bio = vendorStore.bio?.trim() || vendorStore.productAbout?.trim() || "";
    const location = vendorStore.stateInfo?.trim() || vendorStore.storeAddress?.trim() || "";
    const logo = vendorStore.storeLogo;

    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50/80 via-amber-50/25 to-stone-100 px-4 py-10 text-stone-900 sm:py-14">
        <Seo
          title={`About ${name}`}
          description={bio || `Learn about ${name} — browse their products on our platform.`}
          canonical={`/${storeSlug}/about`}
        />
        <div className="mx-auto max-w-4xl">
          {/* Hero */}
          <div className="mb-10 flex flex-col items-center text-center sm:mb-12">
            {logo ? (
              <img
                src={logo}
                alt={name}
                className="mb-5 h-24 w-24 rounded-3xl border-4 border-orange-100 object-cover shadow-xl ring-1 ring-black/5"
              />
            ) : (
              <span className="mb-5 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-rose-500 to-orange-400 text-3xl font-extrabold text-white shadow-xl">
                {name.charAt(0).toUpperCase()}
              </span>
            )}
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-stone-500">
              Our story
            </p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{name}</h1>
            {location && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-stone-500">
                <MapPin className="h-4 w-4 shrink-0 text-orange-400" /> {location}
              </p>
            )}
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            {bio && (
              <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50 ring-1 ring-black/5 sm:p-8 sm:col-span-2">
                <h2 className="text-lg font-extrabold text-stone-900 sm:text-xl">About us</h2>
                <p className="mt-4 text-sm leading-7 text-stone-700 sm:text-base whitespace-pre-line">{bio}</p>
              </div>
            )}

            <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50 ring-1 ring-black/5 sm:p-8">
              <h2 className="text-lg font-extrabold text-stone-900 sm:text-xl">Our commitment</h2>
              <ul className="mt-5 space-y-4">
                {[
                  { title: "Quality products", body: "Every item is curated with care and accuracy." },
                  { title: "Reliable service", body: "We respond quickly and handle orders with transparency." },
                  { title: "Customer first", body: "Your satisfaction drives everything we do." },
                ].map(({ title, body }) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-700 ring-1 ring-orange-200/70">
                      <CheckCircle2 className="h-5 w-5" strokeWidth={2.25} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-extrabold text-stone-900">{title}</span>
                      <span className="mt-1 block text-sm leading-6 text-stone-600">{body}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50 ring-1 ring-black/5 sm:p-8 flex flex-col justify-between">
              <div>
                <h2 className="text-lg font-extrabold text-stone-900 sm:text-xl">Shop with us</h2>
                <p className="mt-4 text-sm leading-6 text-stone-600">
                  Browse our full catalog and find exactly what you need. Fresh listings, clear pricing,
                  and a checkout that works on any device.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/${storeSlug}`}
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3 text-sm font-extrabold text-white shadow-md shadow-orange-300/45 transition hover:from-orange-600 hover:to-amber-600 active:scale-[0.98]"
                >
                  Browse the shop
                </Link>
                <Link
                  to={`/${storeSlug}/contact`}
                  className="inline-flex items-center justify-center rounded-2xl border-2 border-stone-900/90 bg-transparent px-5 py-3 text-sm font-extrabold text-stone-900 transition hover:bg-stone-900 hover:text-white"
                >
                  Get in touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Platform / superuser mode (unchanged) ──────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/80 via-amber-50/25 to-stone-100 px-4 py-10 text-stone-900 sm:py-14">
      <Seo
        title="About | Bakery in Patna, Bihar"
        description="Learn about our bakery in Patna, Bihar—fresh bakes, quality ingredients, and reliable delivery/pickup."
        canonical="/about"
      />
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 text-center sm:mb-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-stone-500">
            Our story
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
            About us
            {storeLabel ? (
              <span className="mt-2 block text-xl font-bold text-stone-600 sm:text-2xl">
                {storeLabel}
              </span>
            ) : null}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-stone-600 sm:text-base">
            {storeLabel ? (
              <>
                <span className="font-semibold text-stone-800">{storeLabel}</span> is part of{" "}
                <span className="font-semibold text-orange-700">MyMomDad (MMD)</span> — a marketplace
                built for real businesses and everyday shoppers.
              </>
            ) : (
              <>
                <span className="font-semibold text-orange-700">MyMomDad (MMD)</span> is a marketplace
                focused on simple, trustworthy shopping and seller tools that actually get used.
              </>
            )}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-800 shadow-sm ring-1 ring-orange-200/80">
              <Sparkles className="h-3.5 w-3.5" />
              Modern commerce
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-stone-700 shadow-sm ring-1 ring-stone-200">
              <Shield className="h-3.5 w-3.5 text-emerald-600" />
              Security-minded
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-stone-700 shadow-sm ring-1 ring-stone-200">
              <Store className="h-3.5 w-3.5 text-orange-600" />
              Store-first UX
            </span>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
          <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50 ring-1 ring-black/5 sm:p-8">
            <h2 className="text-lg font-extrabold text-stone-900 sm:text-xl">Who we are</h2>
            <div className="mt-4 space-y-4 text-sm leading-6 text-stone-700 sm:text-base">
              <p>
                We&apos;re building a platform where vendors can present their catalog with clarity —
                and where customers can browse, compare, and check out without friction.
              </p>
              <p>
                Whether you&apos;re growing a kitchen brand or shopping for staples, our goal is the
                same: fewer surprises, clearer communication, and a flow that respects your time.
              </p>
              <p className="text-stone-600">
                From inventory to payments to post-order support, we invest in the unglamorous
                details that keep day-to-day commerce running smoothly.
              </p>
            </div>
            <Link
              to="/shop"
              className="mt-6 inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3 text-sm font-extrabold text-white shadow-md shadow-orange-300/45 transition hover:from-orange-600 hover:to-amber-600 active:scale-[0.98]"
            >
              Browse the shop
            </Link>
          </div>

          <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50 ring-1 ring-black/5 sm:p-8">
            <h2 className="text-lg font-extrabold text-stone-900 sm:text-xl">
              What sets us apart
            </h2>
            <ul className="mt-5 space-y-4">
              {highlights.map(({ title, body }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-700 ring-1 ring-orange-200/70">
                    <CheckCircle2 className="h-5 w-5" strokeWidth={2.25} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-stone-900">{title}</span>
                    <span className="mt-1 block text-sm leading-6 text-stone-600">{body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 rounded-3xl border border-amber-200/90 bg-gradient-to-r from-amber-50/95 to-orange-50/80 px-5 py-8 text-center shadow-sm ring-1 ring-amber-100/70 sm:mt-12 sm:px-8 sm:py-10">
          <h2 className="text-xl font-extrabold text-stone-900 sm:text-2xl">Our vision</h2>
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-7 text-stone-700 sm:text-base">
            To be the most trusted, seamless link between sellers and buyers — where good products meet
            honest service, and technology stays in the background where it belongs.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center justify-center rounded-2xl border-2 border-stone-900/90 bg-transparent px-5 py-2.5 text-sm font-extrabold text-stone-900 transition hover:bg-stone-900 hover:text-white"
          >
            Get in touch
          </Link>
        </div>
      </div>
    </div>
  );
}

export default About;
